// One library row (mockup 06): thumbnail, title, tags, a status line, the score chip and the
// favourite star. The row opens the puzzle's details.
import type { LibraryItem } from '@zinc/shared';
import { memo } from 'react';
import { Link } from 'react-router';
import { tagsOf } from '../../lib/library';
import { ScoreChip } from '../ScoreChip';
import { Thumbnail } from '../Thumbnail';
import { InProgressMark, SolvedMark, StarIcon } from '../icons';
import { formatTime } from '../solve/SolveHeader';

function StatusLine({ p, otherName }: { p: LibraryItem; otherName: string | null }) {
  const line = 'flex items-center gap-1 text-[12px]';
  if (p.status === 'needs-plugin') {
    return (
      <span className={`${line} font-semibold text-warn-ink`}>
        <span aria-hidden="true" className="h-2 w-2 shrink-0 rounded-[2px] bg-warn" />
        Needs a plugin · playable without checking
      </span>
    );
  }
  const time = (ms: number | null) => (ms != null ? ` · ${formatTime(ms)}` : '');
  if (p.mine.progress === 'solved') {
    return (
      <span className={`${line} text-muted`}>
        <SolvedMark />
        Solved by you{time(p.mine.timeMs)}
      </span>
    );
  }
  if (p.mine.progress === 'in-progress') {
    return (
      <span className={`${line} text-muted`}>
        <InProgressMark />
        In progress{time(p.mine.timeMs)}
      </span>
    );
  }
  if (p.other?.progress === 'solved' && otherName) {
    return (
      <span className={`${line} text-muted`}>
        <SolvedMark />
        Solved by {otherName}
      </span>
    );
  }
  if (p.isNew) {
    return (
      <span className={`${line} text-muted`}>
        <span aria-hidden="true" className="h-2 w-2 shrink-0 rounded-full bg-accent" />
        New
      </span>
    );
  }
  return null;
}

export const PuzzleRow = memo(function PuzzleRow({
  p,
  otherName,
  last,
  onFavourite,
}: {
  p: LibraryItem;
  otherName: string | null;
  last: boolean;
  onFavourite: (p: LibraryItem) => void;
}) {
  return (
    <li
      className={`flex items-center gap-3 py-2.5 pr-1.5 pl-3 ${last ? '' : 'border-b border-row-line'}`}
    >
      <Thumbnail doc={p.preview} size={48} />
      <Link
        to={`/library/${p.id}`}
        className="flex min-w-0 flex-1 flex-col gap-0.5 text-ink no-underline hover:text-ink"
      >
        <span className="truncate text-[15px] font-semibold">{p.title}</span>
        <span className="truncate text-[12px] text-muted">{tagsOf(p)}</span>
        <StatusLine p={p} otherName={otherName} />
      </Link>
      <ScoreChip score={p.status === 'needs-plugin' ? null : p.score} />
      <button
        type="button"
        aria-label={
          p.favourite ? `Remove ${p.title} from favourites` : `Add ${p.title} to favourites`
        }
        aria-pressed={p.favourite}
        onClick={() => onFavourite(p)}
        className={`flex h-10 w-10 shrink-0 items-center justify-center border-0 bg-transparent p-0 ${p.favourite ? 'text-star' : 'text-icon-soft'}`}
      >
        <StarIcon filled={p.favourite} />
      </button>
    </li>
  );
});
